import { useEffect, useRef, useState } from 'react';
import type { HostingPleskAccessView, HostingPleskInventoryView } from '@tosho/contracts';
import {
  hostingPleskInventoryApi,
  hostingPleskInventoryError,
  HostingPleskInventoryApiError,
} from './hosting-plesk-inventory-api';
export function HostingPleskInventoryCard({
  serverId,
  access,
  csrfToken,
  disabled,
  onBusyChange,
  onReauthenticate,
}: {
  readonly serverId: string;
  readonly access: HostingPleskAccessView['access'];
  readonly csrfToken: string;
  readonly disabled: boolean;
  readonly onBusyChange: (busy: boolean) => void;
  readonly onReauthenticate?: (() => void) | undefined;
}): React.JSX.Element {
  const [view, setView] = useState<HostingPleskInventoryView | null>(null),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [reauth, setReauth] = useState(false),
    [visit, setVisit] = useState(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setView(null);
    setError(null);
    void hostingPleskInventoryApi
      .read(serverId)
      .then(
        (result) => {
          if (active) setView(result);
        },
        (failure: unknown) => {
          if (active) setError(hostingPleskInventoryError(failure));
        },
      )
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [serverId, access?.revision, visit]);
  async function refresh(): Promise<void> {
    if (busy || disabled || !view || !access || access.status !== 'ACTIVE') return;
    setBusy(true);
    onBusyChange(true);
    setError(null);
    setReauth(false);
    try {
      const result = await hostingPleskInventoryApi.refresh(
        serverId,
        access.revision,
        view.snapshot?.revision ?? null,
        csrfToken,
      );
      if (alive.current) setView(result);
    } catch (failure) {
      if (!alive.current) return;
      setError(hostingPleskInventoryError(failure));
      setReauth(
        failure instanceof HostingPleskInventoryApiError &&
          failure.code === 'RECENT_AUTHENTICATION_REQUIRED',
      );
      // Un délai HTTP peut expirer après un commit : relire le résultat local
      // sans refaire d'appel Plesk ni renvoyer implicitement une mutation.
      await hostingPleskInventoryApi.read(serverId).then(
        (result) => {
          if (alive.current) setView(result);
        },
        () => undefined,
      );
    } finally {
      if (alive.current) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  }
  return (
    <section
      className="hosting-plesk-inventory"
      aria-label="Inventaire des domaines Plesk"
      aria-busy={busy || loading}
    >
      <h3>Domaines observés</h3>
      {loading ? (
        <p role="status">Chargement du dernier inventaire…</p>
      ) : view ? (
        view.snapshot ? (
          <div role="status">
            <p>
              <strong>
                {view.snapshot.domainCount} domaine{view.snapshot.domainCount > 1 ? 's' : ''}{' '}
                retourné{view.snapshot.domainCount > 1 ? 's' : ''} par Plesk
              </strong>
            </p>
            <p>
              Dernière lecture enregistrée :{' '}
              {new Date(view.snapshot.observedAt).toLocaleString('fr-FR')} · Plesk{' '}
              {view.snapshot.panelVersion}.
            </p>
            {!view.snapshot.accessIsCurrent ? (
              <p>L’accès a changé depuis cette observation. Une nouvelle lecture est nécessaire.</p>
            ) : null}
          </div>
        ) : (
          <p>Aucun inventaire de domaines n’a encore été effectué.</p>
        )
      ) : null}
      <p>
        Cette lecture conserve les domaines retournés par Plesk. Leurs Clients sont choisis ensuite
        dans « Sites observés ». Les sauvegardes sont consultables dans les fiches des serveurs et
        des sites.
      </p>
      {error ? (
        <p className="tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      {reauth && onReauthenticate ? (
        <button type="button" onClick={onReauthenticate}>
          Me reconnecter à Tosho
        </button>
      ) : null}
      <div className="hosting-plesk__actions">
        <button
          type="button"
          disabled={busy || loading || disabled || !view || access?.status !== 'ACTIVE'}
          onClick={() => void refresh()}
        >
          {busy ? 'Lecture des domaines…' : 'Actualiser l’inventaire des domaines'}
        </button>
        <button type="button" disabled={busy || loading} onClick={() => setVisit((n) => n + 1)}>
          Recharger l’état
        </button>
      </div>
    </section>
  );
}
